import { environment } from '../../environments/environment';
import crypto from 'crypto';

export class Util {
  public static isEmpty(value: string | null | undefined) {
    return value === null || value === undefined || `${value}`?.trim() === '';
  }

  public static BaseUrl =
    window.location.origin ||
    `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;

  public static LocaleFromUrl = (): string => {
    const paths = window.location.pathname.split('/');
    const locale = paths.length > 0 ? paths[1] : environment.DEFAULT_LANGUAGE;
    const params = new URL(window.location.href).searchParams;
    return locale === 'en-dth' && params.get('dth') !== null ? (params.get('dth') as string) : locale;
  };

  public static BaseLocation = `${Util.BaseUrl}/${Util.LocaleFromUrl()}`;

  public static CurrentLocation = () => {
    const hash = window.location.hash;
    const searchParts = window.location.href.split('?');
    const search = searchParts.length > 1 ? searchParts[1] : '';
    const route = `${window.location.origin}${window.location.pathname}`;
    return `${route}${hash}${search}`;
  };

  public static AddQueryParams = (path: string, key: string, value: string) => {
    return path.includes('?') ? `'${path}&${key}=${value}` : `${path}?${key}=${value}`;
  };

  public static VoucherDiscount = (price: number, percentage: number = environment.CAPITEC_DISCOUNT_RATE) => {
    return Math.round((price - (price * (percentage / 100 )) + Number.EPSILON));
  }

  public static RemoveAllQueryParams = () => {
    const search = window.location.search.toString();
    // TODO: Clear Hash params as well (For the connect login...)
    if (search.length < 2) {
      return;
    }
    window.history.replaceState(null, '', window.location.href.replace(search, ''));
  };

  public static CheckUrlForConnectHash = (url: string) => {
    const hasAccessToken = url.indexOf('access_token') > -1;
    const hasIdToken = url.indexOf('id_token') > -1;
    return hasAccessToken && hasIdToken;
  };

  public static ParseConnectUrlHash = (url: string) => {
    let index = url.lastIndexOf('#');
    const returnedKeyHasTokenType = url.indexOf('token_type') > -1;
    const returnedKeyHasAccessToken = url.indexOf('access_token') > -1;
    let search = '';

    if (returnedKeyHasTokenType) {
      search = url.substring(index + 1);
    }
    if (search === '' && returnedKeyHasAccessToken) {
      index = url.indexOf('access_token');
      search = url.substring(index);
    }
    if (search !== '' && returnedKeyHasAccessToken) {
      const qsValues = '{"' + search.replace(/&/g, '","').replace(/=/g, '":"') + '"}';
      const qsJson = JSON.parse(qsValues, function (key, value) {
        return key === '' ? value : decodeURIComponent(value);
      });

      if (qsJson.expires_in) {
        const now = new Date(),
          then = new Date(now);
        then.setSeconds(now.getSeconds() + parseInt(qsJson.expires_in));
        qsJson.expires_time = then.toISOString();
      }
      return qsJson;
    }
  };

  public static ArrayUniqBy = <T>(array: T[], key: (value: T) => string) => {
    const seen = new Set();
    return array.filter((item) => {
      const k = key(item);
      return seen.has(k) ? false : seen.add(k);
    });
  };

  public static ArrayInsert = <T>(arr: any[], index: number, ...newItems: T[]): T[] => [
    // part of the array before the specified index
    ...arr.slice(0, index),
    // inserted items
    ...newItems,
    // part of the array after the specified index
    ...arr.slice(index),
  ];

  public static GetExponentialBackoff(count: number, maxDelay: number): number {
    return Math.min(Math.pow(2, count) * 1000, maxDelay);
  }

  public static ObjectToMap = <K, T>(
    object: { [key: number]: number | string } | { [key: string]: number | string }
  ): Map<K, T> => {
    const map = new Map();
    Object.keys(object).forEach((key) => map.set(key, object[key as keyof object]));
    return map;
  };

  public static GetImageAsset = (image: string) => {
    return `${environment.SOURCE_FOLDER}assets/img/${image}`;
  };

  public static GetMediaPath = (image: string) => {
    return environment.production ? image : `https://unificationdev.azurewebsites.net${image}`;
  };

  public static GetScreenWidth = (): number => {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  };

  public static PercentToHex = (p: number) => {
    return Math.round((Math.max(0, Math.min(100, p)) / 100) * 255)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  };

  public static OpenBlob = (fileName: string, blob: Blob, type = 'application/pdf') => {
    const a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([blob], { type }));

    a.download = `${fileName}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  public static LowerCaseKeys = (obj: any) => {
    return Object.keys(obj).reduce((accumulator: any, key) => {
      accumulator[key.charAt(0).toLowerCase() + key.slice(1)] = obj[key];
      return accumulator;
    }, {});
  };

  //Convert to SHA256 Hash value
  public static convertToHash = (value: string) => {
    if (Util.isEmpty(value)) {
      return '';
    } else {
      return crypto.createHash('sha256').update(value).digest('hex');
    }
  };

  public static ScrollToTop() {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }
}
